import * as React from "react"
import { Filter, X } from "lucide-react"
import { Button } from "./button.tsx"
import { cn } from "../../lib/utils.ts"
import { AnimatePresence, motion } from "framer-motion"

interface FilterOption {
  id: string
  label: string
  checked: boolean
}

interface FilterGroup {
  id: string
  label: string
  options: FilterOption[]
}

interface InlineFilterProps {
  groups: FilterGroup[]
  onFilterChange: (groupId: string, optionId: string, checked: boolean) => void
  onReset: () => void
  className?: string
}

export function InlineFilter({ groups, onFilterChange, onReset, className }: InlineFilterProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const filterRef = React.useRef<HTMLDivElement>(null);
  const optionsRef = React.useRef<HTMLDivElement>(null);

  // Handle clicking outside to close the filter
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if any filters are active
  const hasActiveFilters = groups.some(group => 
    group.options.some(option => option.checked)
  );

  // Flatten all options for display
  const allOptions = groups.flatMap(group => 
    group.options.map(option => ({
      ...option,
      groupId: group.id,
      groupLabel: group.label
    }))
  );

  return (
    <motion.div 
      ref={filterRef}
      className={cn("flex items-center h-9", className)}
      layout
      transition={{ duration: 0.2, ease: "easeInOut" }}
    >
      <Button
        variant={hasActiveFilters ? "default" : "outline"}
        size="icon"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "h-9 w-9 shrink-0 transition-all duration-200",
          hasActiveFilters && "bg-purple-600 hover:bg-purple-700",
          isExpanded && !hasActiveFilters && "bg-gray-100 border-gray-300"
        )}
      >
        <Filter className={cn(
          "h-4 w-4 transition-transform duration-200",
          isExpanded && "scale-90"
        )} />
        <span className="sr-only">Filter</span>
      </Button>

      <motion.div 
        ref={optionsRef}
        className="flex items-center gap-2 pl-2"
        initial={false}
        animate={{ 
          width: isExpanded ? 'auto' : 0,
          opacity: isExpanded ? 1 : 0
        }}
        style={{ 
          overflow: 'hidden',
          originX: 0
        }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        <AnimatePresence>
          {isExpanded && allOptions.map((option, index) => (
            <motion.div
              key={`${option.groupId}-${option.id}`}
              initial={{ opacity: 0, x: -10, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -10, scale: 0.95 }}
              transition={{
                duration: 0.15,
                delay: index * 0.03,
                ease: "easeOut"
              }}
            >
              <Button
                variant={option.checked ? "default" : "outline"}
                size="sm"
                className={cn(
                  "whitespace-nowrap transition-colors duration-200",
                  option.checked && "bg-purple-600 hover:bg-purple-700",
                  !option.checked && "hover:bg-gray-100"
                )}
                onClick={() => onFilterChange(option.groupId, option.id, !option.checked)}
              >
                {option.label}
              </Button>
            </motion.div>
          ))}

          {isExpanded && hasActiveFilters && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{
                duration: 0.15,
                delay: allOptions.length * 0.03,
                ease: "easeOut"
              }}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={onReset}
                className="text-gray-500 hover:text-gray-700 whitespace-nowrap"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Reset
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  )
}